import React, {
  FC, useState
} from 'react';
import {
  useTranslation
} from 'react-i18next';

import {
  getTimeAgo, ROUTE
} from 'src/shared/utils';
import {
  NotificationContainer
} from 'src/entities/NotificationsRoute';
import {
  NotificationType
} from 'src/shared/api/notifications/types';
import {
  usePatchAdminApproveSubdomainMutation,
  usePatchAdminDeclineSubdomainMutation,
} from 'src/shared/api/admin/adminApi';
import {
  Button
} from 'src/shared/components';

import * as Style from './Notifications.styles';

interface CustomLoginNotificationProps {
  type: NotificationType;
  createdAt: string;
  isNew: boolean;
  organizationId: string;
  organizationName: string;
  subdomain?: string;
  pendingSubdomain?: string;
}

export const CustomLoginNotification: FC<CustomLoginNotificationProps> = ({
  type,
  createdAt,
  isNew,
  organizationId,
  organizationName,
  subdomain,
  pendingSubdomain,
}) => {
  const {
    t
  } = useTranslation();

  const [isDone, setIsDone] = useState(false);

  const [approveSubdomain, {
    isLoading: isApproving
  }] = usePatchAdminApproveSubdomainMutation();

  const [declineSubdomain, {
    isLoading: isDeclining
  }] = usePatchAdminDeclineSubdomainMutation();

  const handleApprove = async () => {
    await approveSubdomain(organizationId);
    setIsDone(true);
  };

  const handleDecline = async () => {
    await declineSubdomain(organizationId);
    setIsDone(true);
  };

  return (
    <NotificationContainer $isNew={isNew}>
      <Style.Description>
        <Style.NotificationHeader>
          <Style.NotificationDate>
            {`${getTimeAgo(createdAt)} ${t('date.ago')}`}
          </Style.NotificationDate>
        </Style.NotificationHeader>

        {type === NotificationType.CUSTOM_LOGIN_ACCESS_GRANTED && (
          <>
            <Style.NotificationText>
              {'Custom login enabled for '}

              <Style.NotificationBoldText>
                {organizationName}
              </Style.NotificationBoldText>

              {'. Subdomain: '}

              <Style.NotificationBoldText>
                {subdomain}
              </Style.NotificationBoldText>
            </Style.NotificationText>

            <Button
              variant="big-grey-bordered"
              className="w-max text-15-20 font-bold !px-3 !py-2 !h-9"
              to={`/${ROUTE.ORGANISATION}/${organizationId}`}
              component="link"
            >
              {t('customLogin.setupLogin')}
            </Button>
          </>
        )}

        {type === NotificationType.CUSTOM_LOGIN_SUBDOMAIN_PENDING && (
          <>
            <Style.NotificationText>
              <Style.NotificationBoldText>
                {organizationName}
              </Style.NotificationBoldText>

              {' requested subdomain: '}

              <Style.NotificationBoldText>
                {pendingSubdomain}
              </Style.NotificationBoldText>
            </Style.NotificationText>

            {!isDone && (
              <Style.ButtonsContainer>
                <Style.ButtonToConfirm
                  $isConfirm
                  type="button"
                  disabled={isApproving || isDeclining}
                  onClick={handleApprove}
                >
                  {t('buttons.approve')}
                </Style.ButtonToConfirm>

                <Style.ButtonToConfirm
                  type="button"
                  disabled={isApproving || isDeclining}
                  onClick={handleDecline}
                >
                  {t('buttons.decline')}
                </Style.ButtonToConfirm>
              </Style.ButtonsContainer>
            )}
          </>
        )}

        {type === NotificationType.CUSTOM_LOGIN_SUBDOMAIN_APPROVED && (
          <Style.NotificationText>
            {'Subdomain '}

            <Style.NotificationBoldText>{subdomain}</Style.NotificationBoldText>

            {' approved for '}

            <Style.NotificationBoldText>
              {organizationName}
            </Style.NotificationBoldText>
          </Style.NotificationText>
        )}

        {type === NotificationType.CUSTOM_LOGIN_SUBDOMAIN_REJECTED && (
          <Style.NotificationText>
            {'Subdomain request declined for '}

            <Style.NotificationBoldText>
              {organizationName}
            </Style.NotificationBoldText>
          </Style.NotificationText>
        )}
      </Style.Description>
    </NotificationContainer>
  );
};
